// @flow
import { of } from 'rxjs';
import { mergeMap, map, catchError, filter } from 'rxjs/operators';
import update from 'immutability-helper';
import { setNewAlert } from '../alert/duck';
import { ofType } from 'redux-observable';
import { push } from 'react-router-redux';
import {
  endpoints,
  getPurchaseApi,
  getSaleApi,
  getMessagesApi,
  postMessageApi,
} from './api';
// Purchase
import purchaseReducer from './Purchase/duck';
import { initialState as purchaseInitialState } from './Purchase/duck';
import { actions as unconfirmedPaymentActions } from './Purchase/unconfirmed-payment/duck';
import { actions as courierFlowActions } from './Purchase/courier-flow/duck';
import { actions as lookingForTravelerActions } from './Purchase/looking-for-traveler/duck';
import {
  actions as registerPurchaseActions,
} from './Purchase/register-purchase/duck';
import { actions as inTransitAction } from './Purchase/in-transit/duck';
import { actions as feedbackAction } from './Purchase/feedback/duck';
// Sale
import saleReducer from './Sale/duck';
import { initialState as saleInitialState } from './Sale/duck';
import { actions as reservedActions } from './Sale/reserved/duck';
import { actions as receivedActions } from './Sale/received/duck';
import { actions as purchasedActions } from './Sale/purchased/duck';
import { actions as deliverActions } from './Sale/deliver/duck';
import { actions as completedActions } from './Sale/completed/duck';

import { actions as notificationsActions } from '../navbar/notifications/duck';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Order, OrderMessage, Notification } from '../../entities';
import type { PurchaseState } from './Purchase/duck';
import type { SaleState } from './Sale/duck';

export type OrderType = 'purchase' | 'sale';

type OrderAction = {
  +type: string;
  +error?: HTTPError;
  +order?: Order;
  +orderId?: string;
  +orderType?: OrderType;
  +messages?: OrderMessage[];
  +message?: OrderMessage;
  +postMessageError?: HTTPError;
  +queuedMessage?: OrderMessage;
  +queuedMessageKey?: number;
};

export type OrderState = {
  +isFetchingOrder?: boolean;
  +order?: Order;
  +fetchOrderError?: HTTPError;
  +isFetchingMessages?: boolean;
  +messages?: any[];
  +fetchMessagesError?: HTTPError;
  +purchase: PurchaseState;
  +sale: SaleState;
  +isPostingMessage?: boolean;
  +postMessageError?: HTTPError;
};

// Actions
const ORDER_FETCH_ORDER_REQUEST = 'ORDER_FETCH_ORDER_REQUEST';
const ORDER_FETCH_ORDER_SUCCESS = 'ORDER_FETCH_ORDER_SUCCESS';
const ORDER_FETCH_ORDER_ERROR = 'ORDER_FETCH_ORDER_ERROR';

const ORDER_MESSAGES_RESET = 'ORDER_MESSAGES_RESET';
const ORDER_FETCH_MESSAGES_REQUEST = 'ORDER_FETCH_MESSAGES_REQUEST';
const ORDER_FETCH_MESSAGES_SUCCESS = 'ORDER_FETCH_MESSAGES_SUCCESS';
const ORDER_FETCH_MESSAGES_ERROR = 'ORDER_FETCH_MESSAGES_ERROR';
const ORDER_ADD_MESSAGE = 'ORDER_ADD_MESSAGE';
const ORDER_QUEUE_MESSAGE = 'ORDER_QUEUE_MESSAGE';
const ORDER_UNQUEUE_MESSAGE = 'ORDER_UNQUEUE_MESSAGE';
const ORDER_POST_MESSAGE_REQUEST = 'ORDER_POST_MESSAGE_REQUEST';
// No success, re-using add message
const ORDER_POST_MESSAGE_ERROR = 'ORDER_POST_MESSAGE_ERROR';

const ORDER_RESET_STATE = 'ORDER_RESET_STATE';

const NOOP = 'NOOP';

// Action Creators

export function fetchOrder(orderId: string, orderType: OrderType): OrderAction {
  return {
    type: ORDER_FETCH_ORDER_REQUEST,
    orderId,
    orderType,
  };
}

export function fetchOrderSuccess(order: Order): OrderAction {
  return {
    type: ORDER_FETCH_ORDER_SUCCESS,
    order,
  };
}

export function fetchOrderErrorFn(error: HTTPError): OrderAction {
  return {
    type: ORDER_FETCH_ORDER_ERROR,
    error,
  };
}

export function fetchMessages(orderId: string): OrderAction {
  return {
    type: ORDER_FETCH_MESSAGES_REQUEST,
    orderId,
  };
}

export function fetchMessagesSuccess(messages: OrderMessage[]): OrderAction {
  return {
    type: ORDER_FETCH_MESSAGES_SUCCESS,
    messages,
  };
}

export function fetchMessagesErrorFn(error: HTTPError): OrderAction {
  return {
    type: ORDER_FETCH_MESSAGES_ERROR,
    error,
  }
}

export function resetMessages(): OrderAction {
  return {
    type: ORDER_MESSAGES_RESET,
  };
}

export function addMessage(message: OrderMessage): OrderAction {
  return {
    type: ORDER_ADD_MESSAGE,
    message,
  };
}

export function queueMessage(queuedMessage: OrderMessage): OrderAction {
  return {
    type: ORDER_QUEUE_MESSAGE,
    queuedMessage,
  };
}

export function unQueueMessage(queuedMessageKey: number): OrderAction {
  return {
    type: ORDER_UNQUEUE_MESSAGE,
    queuedMessageKey,
  };
}

export function postMessage(orderId: string, message: OrderMessage): OrderAction {
  return {
    type: ORDER_POST_MESSAGE_REQUEST,
    orderId,
    message,
  };
}

export function postMessageError(postMessageError: HTTPError): OrderAction {
  return {
    type: ORDER_POST_MESSAGE_ERROR,
    postMessageError,
  };
}

function noop(): OrderAction {
  return {
    type: NOOP,
  }
}

export function resetState(): OrderAction {
  return {
    type: ORDER_RESET_STATE,
  };
}

export const actions = {
  ORDER_FETCH_ORDER_REQUEST,
  ORDER_FETCH_ORDER_SUCCESS,
  ORDER_FETCH_ORDER_ERROR,
  ORDER_MESSAGES_RESET,
  ORDER_FETCH_MESSAGES_REQUEST,
  ORDER_FETCH_MESSAGES_SUCCESS,
  ORDER_FETCH_MESSAGES_ERROR,
  ORDER_ADD_MESSAGE,
  ORDER_QUEUE_MESSAGE,
  ORDER_UNQUEUE_MESSAGE,
  ORDER_POST_MESSAGE_REQUEST,
  ORDER_POST_MESSAGE_ERROR,
  ORDER_RESET_STATE,
  fetchOrder,
  fetchOrderSuccess,
  fetchOrderErrorFn,
  resetMessages,
  fetchMessages,
  fetchMessagesSuccess,
  fetchMessagesErrorFn,
  addMessage,
  queueMessage,
  unQueueMessage,
  postMessage,
  postMessageError,
  resetState,
};

export const initialState: OrderState = {
  purchase: purchaseInitialState,
  sale: saleInitialState,
};

// Reducer
export default function reducer(state: OrderState = initialState,
                                action: OrderAction): OrderState {
  switch (action.type) {
    case ORDER_FETCH_ORDER_REQUEST:
      return update(state, {
        $unset: ['order', 'fetchOrderError'],
        isFetchingOrder: { $set: true },
      });
    case ORDER_FETCH_ORDER_SUCCESS:
      return update(state, {
        $unset: ['isFetchingOrder', 'fetchOrderError'],
        order: { $set: action.order },
      });
    case ORDER_FETCH_ORDER_ERROR:
      return update(state, {
        $unset: ['isFetchingOrder'],
        fetchOrderError: { $set: action.error },
      });
    case ORDER_MESSAGES_RESET:
      return update(state, {
        $unset: ['messages'],
      });
    case ORDER_FETCH_MESSAGES_REQUEST:
      return update(state, {
        isFetchingMessages: { $set: true },
      });
    case ORDER_FETCH_MESSAGES_SUCCESS:
      return update(state, {
        $unset: ['isFetchingMessages', 'fetchMessagesError'],
        messages: { $set: action.messages },
      });
    case ORDER_ADD_MESSAGE:
      return update(state, {
        messages: {
          $push: [action.message],
        },
      });
    case ORDER_QUEUE_MESSAGE:
      return update(state, {
        messages: {
          $set: [...(state.messages || []), action.queuedMessage],
        }
      });
    case ORDER_UNQUEUE_MESSAGE:
      if (state.messages) {
        const messageIndex = state.messages.findIndex((message: OrderMessage) =>
          message.createdAt === action.queuedMessageKey);
        return update(state, {
          messages: {
            [messageIndex]: {
              loading: { $set: false },
            },
          },
        });
      }
      return state;
    case ORDER_POST_MESSAGE_REQUEST:
      return update(state, {
        isPostingMessage: { $set: true },
      });
    case ORDER_POST_MESSAGE_ERROR:
      return update(state, {
        postMessageError: { $set: action.postMessageError },
      });
    case ORDER_FETCH_MESSAGES_ERROR:
      return update(state, {
        $unset: ['isFetchingMessages'],
        fetchMessagesError: { $set: action.error },
      });
    // Purchase
    case unconfirmedPaymentActions.PURCHASE_UNCONFIRMED_PAYMENT_DELETE_ORDER_REQUEST:
    case unconfirmedPaymentActions.PURCHASE_UNCONFIRMED_PAYMENT_DELETE_ORDER_ERROR:
    case unconfirmedPaymentActions.PURCHASE_UNCONFIRMED_PAYMENT_RESET_STATE:
    case lookingForTravelerActions.LOOKING_FOR_TRAVELERS_CANCEL_ORDER_REQUEST:
    case lookingForTravelerActions.LOOKING_FOR_TRAVELERS_CANCEL_ORDER_ERROR:
    case registerPurchaseActions.REGISTER_PURCHASE_UPLOAD_INVOICE_FILE_REQUEST:
    case registerPurchaseActions.REGISTER_PURCHASE_UPLOAD_INVOICE_REQUEST:
    case registerPurchaseActions.REGISTER_PURCHASE_UPLOAD_INVOICE_ERROR:
    case registerPurchaseActions.REGISTER_PURCHASE_POST_IDENTITY_REQUEST:
    case registerPurchaseActions.REGISTER_PURCHASE_POST_IDENTITY_SUCCESS:
    case registerPurchaseActions.REGISTER_PURCHASE_POST_IDENTITY_ERROR:
    case registerPurchaseActions.REGISTER_PURCHASE_PROGRESS_PURCHASE_REQUEST:
    case registerPurchaseActions.REGISTER_PURCHASE_PROGRESS_PURCHASE_ERROR:
    case courierFlowActions.COURIER_FLOW_POST_SET_TRACKING_NUMBER_REQUEST:
    case courierFlowActions.COURIER_FLOW_POST_SET_TRACKING_NUMBER_ERROR:
    case inTransitAction.IN_TRANSIT_POST_CONFIRMATION_REQUEST:
    case inTransitAction.IN_TRANSIT_POST_CONFIRMATION_ERROR:
    case feedbackAction.FEEDBACK_POST_FEEDBACK_REQUEST:
    case feedbackAction.FEEDBACK_POST_FEEDBACK_ERROR:
      return update(state, {
        purchase: { $set: purchaseReducer((state: any).purchase, (action: any)) },
      });
    case unconfirmedPaymentActions.PURCHASE_UNCONFIRMED_PAYMENT_DELETE_ORDER_SUCCESS:
    case courierFlowActions.COURIER_FLOW_POST_SET_TRACKING_NUMBER_SUCCESS:
    case lookingForTravelerActions.LOOKING_FOR_TRAVELERS_CANCEL_ORDER_SUCCESS:
    case registerPurchaseActions.REGISTER_PURCHASE_UPLOAD_INVOICE_SUCCESS:
    case registerPurchaseActions.REGISTER_PURCHASE_PROGRESS_PURCHASE_SUCCESS:
    case inTransitAction.IN_TRANSIT_POST_CONFIRMATION_SUCCESS:
    case feedbackAction.FEEDBACK_POST_FEEDBACK_SUCCESS:
      return update(state, {
        purchase: { $set: purchaseReducer((state: any).purchase, (action: any)) },
        order: { $set: action.order },
      });
    // Sale
    case reservedActions.RESERVED_UPLOAD_INVOICE_FILE_REQUEST:
    case reservedActions.RESERVED_UPLOAD_INVOICE_ERROR:
    case reservedActions.RESERVED_PROGRESS_PURCHASE_REQUEST:
    case reservedActions.RESERVED_PROGRESS_PURCHASE_ERROR:
    case reservedActions.RESERVED_CANCEL_RESERVATION_REQUEST:
    case reservedActions.RESERVED_CANCEL_RESERVATION_SUCCESS:
    case reservedActions.RESERVED_CANCEL_RESERVATION_ERROR:
    case receivedActions.RECEIVED_POST_ARRIVED_REQUEST:
    case receivedActions.RECEIVED_POST_ARRIVED_ERROR:
    case purchasedActions.PURCHASED_POST_SET_TRACKING_NUMBER_REQUEST:
    case purchasedActions.PURCHASED_POST_SET_TRACKING_NUMBER_ERROR:
    case purchasedActions.PURCHASED_POST_CONFIRM_ITEM_RECEIVED_REQUEST:
    case purchasedActions.PURCHASED_POST_CONFIRM_ITEM_RECEIVED_ERROR:
    case purchasedActions.PURCHASED_POST_PROGRESS_RECEIVE_REQUEST:
    case purchasedActions.PURCHASED_POST_PROGRESS_RECEIVE_ERROR:
    case purchasedActions.PURCHASED_FETCH_CANCELATION_MOTIVES_REQUEST:
    case purchasedActions.PURCHASED_FETCH_CANCELATION_MOTIVES_SUCCESS:
    case purchasedActions.PURCHASED_FETCH_CANCELATION_MOTIVES_ERROR:
    case completedActions.COMPLETED_FETCH_DISBURSEMENT_REQUEST:
    case completedActions.COMPLETED_FETCH_DISBURSEMENT_SUCCESS:
    case completedActions.COMPLETED_FETCH_DISBURSEMENT_ERROR:
    case purchasedActions.PURCHASED_CANCEL_ITEM_REQUEST:
    case purchasedActions.PURCHASED_CANCEL_ITEM_ERROR:
      return update(state, {
        sale: { $set: saleReducer((state: any).sale, (action: any)) },
      });
    case reservedActions.RESERVED_UPLOAD_INVOICE_SUCCESS:
    case reservedActions.RESERVED_PROGRESS_PURCHASE_SUCCESS:
    case receivedActions.RECEIVED_POST_ARRIVED_SUCCESS:
    case purchasedActions.PURCHASED_POST_SET_TRACKING_NUMBER_SUCCESS:
    case purchasedActions.PURCHASED_POST_CONFIRM_ITEM_RECEIVED_SUCCESS:
    case purchasedActions.PURCHASED_POST_PROGRESS_RECEIVE_SUCCESS:
    case purchasedActions.PURCHASED_CANCEL_ITEM_SUCCESS:
      return update(state, {
        sale: { $set: saleReducer((state: any).sale, (action: any)) },
        order: { $set: action.order },
      });
    case deliverActions.DELIVER_POST_PROGRESS_COURIER_REQUEST:
    case deliverActions.DELIVER_POST_PROGRESS_INVOICE_SUCCESS:
    case deliverActions.DELIVER_POST_PROGRESS_QEMPO_REQUEST:
    case deliverActions.DELIVER_POST_PROGRESS_ERROR:
    case deliverActions.DELIVER_FETCH_COURIERS_REQUEST:
    case deliverActions.DELIVER_FETCH_COURIERS_SUCCESS:
    case deliverActions.DELIVER_FETCH_COURIERS_ERROR:
      return update(state, {
        sale: { $set: saleReducer(state.sale, (action: any)) },
      });
    case deliverActions.DELIVER_POST_PROGRESS_SUCCESS:
      return update(state, {
        sale: { $set: saleReducer(state.sale, (action: any)) },
        order: { $set: action.order },
      });
    case ORDER_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchOrderEpic(action$: Observable<OrderAction>): Observable<OrderAction> {
  return action$.pipe(
    ofType(ORDER_FETCH_ORDER_REQUEST),
    mergeMap(({ orderId, orderType }) => {
      const api = orderType === 'purchase' ? getPurchaseApi : getSaleApi;
      const ep = orderType === 'purchase' ? endpoints.GET_PURCHASE : endpoints.GET_SALE;
      return api(undefined, {
        route: ep.replace(':id', orderId),
      }).pipe(
        map((order: Order) => fetchOrderSuccess(order)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(fetchOrderErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(fetchOrderErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
            );
          } else if (e.code === 404) {
            /*return of(fetchOrderErrorFn(e),
              setNewAlert('order', 'error', undefined, 'httpErrors.post.400'),
            );*/
            //return of(push('/'));
            return of(fetchOrderErrorFn(e));
          }
          return of(fetchOrderErrorFn(e));
        }));
    }));
}


export function fetchMessagesEpic(action$: Observable<OrderAction>): Observable<OrderAction> {
  return action$.pipe(
    ofType(ORDER_FETCH_MESSAGES_REQUEST),
    mergeMap(({ orderId }) => getMessagesApi(undefined, {
        route: endpoints.GET_MESSAGES.replace(':id', orderId)
      }).pipe(
      map((response: any[]) => fetchMessagesSuccess(response)),
      catchError((e: HTTPError) => of(fetchMessagesErrorFn(e))))
    ));
}

export function postOrderMessageEpic(action$: Observable<OrderAction>): Observable<OrderAction> {
  return action$.pipe(
    ofType(ORDER_POST_MESSAGE_REQUEST),
    mergeMap(({ orderId, message }) =>
      postMessageApi({ message: message.message, imageId: message.image }, {
        route: endpoints.POST_MESSAGE.replace(':id', orderId)
      }).pipe(
        map(() => noop()),
        catchError((e: HTTPError) => of(postMessageError(e))))
    ));
}

export function updateTransactionRealTimeEpic(action$: Observable<OrderAction>): Observable<OrderAction> {
  return action$.pipe(
    ofType(notificationsActions.NOTIFICATIONS_NEW_REALTIME),
    filter((notification: Notification) => !!notification.order),
    map((notification: Notification) => {
      return noop();
    }));
}



// WEBPACK FOOTER //
// ./src/modules/order/duck.js